import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const BOGOS_CART_UPSELL_ROOT_SELECTOR = '.bogos-cart-upsell-root';
const BOGOS_CART_UPSELL_WIDGET_SELECTOR = '.bogos-cart-upsell-widget';
const BOGOS_CART_UPSELL_CARD_SELECTOR = '.bogos-cart-upsell-card';
let bogosSyncFrame;
let bogosMarkupSignature = '';

const initCartTopPicks = (root = document) => {
  root.querySelectorAll('[data-cart-top-picks]').forEach((section) => {
    if (section.__cartTopPicksInitialized) return;
    section.__cartTopPicksInitialized = true;

    section.addEventListener('submit', handleCartTopPicksSubmit);
    section.addEventListener('click', handleCartTopPicksDotClick);
    section.addEventListener('scroll', handleCartTopPicksMobileScroll, true);
    syncBogosCartTopPicks();
  });
};

const startBogosObserver = () => {
  if (!document.body) return;
  bogosObserver.observe(document.body, { childList: true, subtree: true });
  syncBogosCartTopPicks();
};

const bogosObserver = new MutationObserver(() => scheduleBogosCartTopPicksSync());

const scheduleBogosCartTopPicksSync = () => {
  if (bogosSyncFrame) return;

  bogosSyncFrame = window.requestAnimationFrame(() => {
    bogosSyncFrame = undefined;
    syncBogosCartTopPicks();
  });
};

const syncBogosCartTopPicks = () => {
  const section = document.querySelector('[data-cart-top-picks]');
  if (!(section instanceof HTMLElement)) return;

  const maxProducts = Number(section.dataset.maxProducts || 8);
  const root = getCartPageBogosRoot();
  const cards = root instanceof HTMLElement ? getOrderedBogosCards(root) : [];
  const renderedCards = cards.map(renderBogosSourceCard).filter((card) => card !== null);
  const visibleCards = maxProducts > 0 ? renderedCards.slice(0, maxProducts) : renderedCards;
  const markup = visibleCards.map((card) => card.markup).join('');
  const signature = `${visibleCards.map((card) => card.variantId).join(',')}:${markup}`;

  toggleCartTopPicks(section, visibleCards.length > 0);
  if (signature === bogosMarkupSignature) return;

  bogosMarkupSignature = signature;
  renderCartTopPicks(section, visibleCards);
};

const getCartPageBogosRoot = () => Array.from(document.querySelectorAll(BOGOS_CART_UPSELL_ROOT_SELECTOR))
  .find((root) => root instanceof HTMLElement && !root.matches('[data-surface="cart_drawer"]') && !root.closest('cart-drawer-component'));

const toggleCartTopPicks = (section, isVisible) => {
  section.querySelector('[data-cart-top-picks-content]')?.toggleAttribute('hidden', !isVisible);
};

const handleCartTopPicksDotClick = (event) => {
  const dot = event.target instanceof Element ? event.target.closest('[data-cart-top-picks-dot]') : null;
  if (!(dot instanceof HTMLElement)) return;

  const section = dot.closest('[data-cart-top-picks]');
  const list = section?.querySelector('[data-cart-top-picks-mobile-primary]');
  const items = list?.querySelectorAll('.cart-top-picks__item');
  const index = Number(dot.dataset.cartTopPicksDot || 0);
  const item = items?.[index];

  if (!(list instanceof HTMLElement) || !(item instanceof HTMLElement)) return;

  list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior: 'smooth' });
  setActiveMobileDot(section, index);
};

const handleCartTopPicksMobileScroll = (event) => {
  if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-cart-top-picks-mobile-primary]')) return;

  const list = event.target;
  const section = list.closest('[data-cart-top-picks]');
  const items = Array.from(list.querySelectorAll('.cart-top-picks__item'));
  if (!section || !items.length) return;

  const index = items.reduce((closestIndex, item, itemIndex) => {
    const closestDistance = Math.abs(items[closestIndex].getBoundingClientRect().left - list.getBoundingClientRect().left);
    const itemDistance = Math.abs(item.getBoundingClientRect().left - list.getBoundingClientRect().left);
    return itemDistance < closestDistance ? itemIndex : closestIndex;
  }, 0);

  setActiveMobileDot(section, index);
};

const setActiveMobileDot = (section, index) => {
  section?.querySelectorAll('[data-cart-top-picks-dot]').forEach((dot, dotIndex) => {
    dot.classList.toggle('cart-top-picks__mobile-dot--active', dotIndex === index);
  });
};

const handleCartTopPicksSubmit = async (event) => {
  const form = event.target instanceof Element ? event.target.closest('[data-cart-top-picks-form]') : null;
  if (!(form instanceof HTMLFormElement)) return;

  event.preventDefault();

  const section = form.closest('[data-cart-top-picks]');
  const button = form.querySelector('[data-cart-top-picks-add]');
  const formData = new FormData(form);
  const sectionIds = getCartSectionIds();
  const quantity = Number(formData.get('quantity')) || 1;

  if (sectionIds.length) {
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
  }

  setButtonState(button, 'loading');

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', fetchConfig('javascript', { body: formData }));
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add item to cart');
    }

    const itemCount = getCartItemCountFromSections(data.sections, sectionIds) ?? quantity;
    setButtonState(button, 'added');

    section?.dispatchEvent(
      new CartAddEvent({ item_count: itemCount }, formData.get('id')?.toString(), {
        source: 'cart-top-picks',
        itemCount: quantity,
        productId: data.product_id?.toString(),
        variantId: data.variant_id?.toString() || formData.get('id')?.toString(),
        sections: data.sections,
      })
    );

    scheduleBogosCartTopPicksSync();
    window.setTimeout(syncBogosCartTopPicks, 250);
    window.setTimeout(syncBogosCartTopPicks, 750);

    window.setTimeout(() => setButtonState(button, 'idle'), 900);
  } catch (error) {
    console.error(error);
    setButtonState(button, 'error');
    window.setTimeout(() => setButtonState(button, 'idle'), 1200);
  }
};

const renderCartTopPicks = (section, cards) => {
  const desktop = section.querySelector('[data-cart-top-picks-desktop]');
  const mobilePrimary = section.querySelector('[data-cart-top-picks-mobile-primary]');
  const mobileCards = cards.slice(0, 5);

  if (desktop) desktop.innerHTML = cards.map((card) => card.markup).join('');
  if (mobilePrimary) mobilePrimary.innerHTML = mobileCards.map((card) => card.markup).join('');
  renderMobileDots(section, mobileCards.length);
};

const renderBogosSourceCard = (card) => {
  const image = card.querySelector('img');
  const form = card.querySelector('form');
  const variantInput = form?.querySelector('input[name="id"]') || card.querySelector('input[name="id"]');
  const variantId = variantInput instanceof HTMLInputElement
    ? variantInput.value
    : card.querySelector('[data-variant-id], [data-variant]')?.getAttribute('data-variant-id') || card.querySelector('[data-variant-id], [data-variant]')?.getAttribute('data-variant') || '';
  if (!variantId) return null;

  const link = card.querySelector('a[href]');
  const url = link instanceof HTMLAnchorElement ? link.href : '#';
  const title = getBogosCardTitle(card, link);
  const price = getBogosCardPrice(card);
  const imageSrc = image instanceof HTMLImageElement ? getBestImageUrl(image, 400) : '';
  const imageAlt = image instanceof HTMLImageElement ? image.alt || title : title;

  return {
    variantId,
    markup: `
    <div class="cart-top-picks__item">
      <article class="hoover-related-product-card">
        <div class="hoover-related-product-card__inner">
          <a class="hoover-related-product-card__image-link" href="${escapeAttribute(url)}" aria-label="${escapeAttribute(title)}">
            ${imageSrc ? `<img loading="lazy" class="hoover-related-product-card__image" src="${escapeAttribute(imageSrc)}" alt="${escapeAttribute(imageAlt)}">` : ''}
          </a>
          <div class="hoover-related-product-card__details">
            <div class="hoover-related-product-card__price" aria-label="Price">
              ${price ? `<span class="hoover-related-product-card__price-current">${escapeHtml(price)}</span>` : ''}
            </div>
            <h3 class="hoover-related-product-card__title"><a href="${escapeAttribute(url)}">${escapeHtml(title)}</a></h3>
            <div class="hoover-related-product-card__cta">
              <form method="post" action="${escapeAttribute(window.Theme?.routes?.cart_add_url || '/cart/add')}" class="hoover-related-product-card__quick-add" data-cart-top-picks-form>
                <input type="hidden" name="id" value="${escapeAttribute(variantId)}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="hoover-related-product-card__notify-button" data-cart-top-picks-add>
                  <span class="svg-wrapper" aria-hidden="true">+</span>
                  <span class="cart-top-picks__add-label" data-cart-top-picks-add-label>Add</span>
                  <span class="cart-top-picks__spinner" aria-hidden="true"></span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </article>
    </div>
  `,
  };
};

const getOrderedBogosCards = (root) => {
  const widgets = Array.from(root.querySelectorAll(BOGOS_CART_UPSELL_WIDGET_SELECTOR));
  if (!widgets.length) return Array.from(root.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR));

  return widgets.reverse().flatMap((widget) => Array.from(widget.querySelectorAll(BOGOS_CART_UPSELL_CARD_SELECTOR)));
};

const getBogosCardTitle = (card, link) => {
  const titleElement = card.querySelector('.bogos-cart-upsell-product-title, .bogos-cart-upsell-card-title, .bogos-cart-upsell-name, [class*="title"], [class*="name"]');
  const title = titleElement?.textContent?.trim() || link?.textContent?.trim() || card.querySelector('img')?.getAttribute('alt')?.trim();
  return title
    ?.replace(/^\s*\d+\s*(?:x|×)\s*/i, '')
    .replace(/\s*(?:x|×)\s*\d+\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim() || 'Recommended product';
};

const getBogosCardPrice = (card) => {
  const priceElement = card.querySelector('.bogos-cart-upsell-price, .bogos-cart-upsell-product-price, .bogos-cart-upsell-card-price, [class*="price"]');
  const priceText = priceElement?.textContent?.trim() || card.textContent?.match(/\$\s?\d+(?:\.\d{2})?/)?.[0];
  return priceText || '';
};

const getBestImageUrl = (image, width) => {
  const srcsetUrl = getLargestSrcsetUrl(image.getAttribute('srcset') || '');
  return normalizeImageUrl(srcsetUrl || image.getAttribute('src') || image.src, width);
};

const getLargestSrcsetUrl = (srcset) => {
  if (!srcset) return '';

  return srcset
    .split(',')
    .map((entry) => {
      const [url, descriptor] = entry.trim().split(/\s+/);
      const width = Number(descriptor?.replace('w', '') || 0);
      return { url, width };
    })
    .filter((entry) => entry.url)
    .sort((a, b) => b.width - a.width)[0]?.url || '';
};

const normalizeImageUrl = (url, width) => {
  if (!url) return '';

  const normalizedUrl = new URL(url, window.location.origin);
  normalizedUrl.searchParams.set('width', width.toString());
  normalizedUrl.searchParams.set('height', width.toString());
  normalizedUrl.searchParams.set('crop', 'center');
  return normalizedUrl.toString();
};

const renderMobileDots = (section, count) => {
  section.querySelectorAll('.cart-top-picks__mobile-dots').forEach((dots) => {
    if (count <= 1) {
      dots.innerHTML = '';
      return;
    }

    dots.innerHTML = Array.from({ length: count }, (_, index) => (
      `<button type="button" class="cart-top-picks__mobile-dot${index === 0 ? ' cart-top-picks__mobile-dot--active' : ''}" data-cart-top-picks-dot="${index}" aria-label="Show top pick ${index + 1}"></button>`
    )).join('');
  });
};

const setButtonState = (button, state) => {
  if (!(button instanceof HTMLButtonElement)) return;

  const label = button.querySelector('[data-cart-top-picks-add-label]');

  if (state === 'loading') {
    button.style.width = `${button.offsetWidth}px`;
  } else if (state === 'idle') {
    button.style.width = '';
  }

  button.dataset.state = state;
  button.disabled = state === 'loading';

  if (label) {
    label.textContent = state === 'added' ? 'Added' : state === 'error' ? 'Error' : 'Add';
  }
};

const getCartSectionIds = () => Array.from(document.querySelectorAll('cart-items-component'))
  .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : undefined)
  .filter((sectionId) => Boolean(sectionId));

const getCartItemCountFromSections = (sections, sectionIds) => {
  if (!sections) return null;

  for (const sectionId of sectionIds) {
    const sectionHtml = sections[sectionId];
    if (!sectionHtml) continue;

    const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const count = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
    if (count) return Number(count);
  }

  return null;
};

const imageUrl = (url, width) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}`;
};

const escapeHtml = (value) => value.replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#39;',
  '"': '&quot;',
}[character]));

const escapeAttribute = escapeHtml;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCartTopPicks();
    startBogosObserver();
  }, { once: true });
} else {
  initCartTopPicks();
  startBogosObserver();
}

document.addEventListener('shopify:section:load', (event) => {
  initCartTopPicks(event.target);
});
